import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, SearchIcon, PlusIcon, ChevronRightIcon, BuildingIcon, SmartphoneIcon, ShieldIcon, StoreIcon, UserIcon, Edit3Icon, WalletIcon, CheckIcon, XIcon, ShareIcon, CalendarIcon, CopyIcon, ArrowRightIcon, RefreshCwIcon, BellIcon, ChevronDownIcon, CheckCircleIcon, CheckCircle2Icon, InfoIcon, FingerprintIcon, ScanFaceIcon, HomeIcon, ContactIcon, Image as ImageIcon, StarIcon, PencilIcon, Trash2Icon, AlertTriangleIcon, CreditCardIcon } from 'lucide-react';
import { NotificationPanel } from './NotificationPanel';
import { useTheme } from '../utils/theme';
interface SendMoneyScreenProps {
  onBack: () => void;
  onNavigateHome?: () => void;
}
type Step = 'select_recipient' | 'select_bank' | 'bank_Payees' | 'add_Payee' | 'verify_Payee' | 'Payee_saved' | 'enter_amount' | 'review' | 'mpin' | 'processing' | 'success';
interface Recipient {
  id: string;
  name: string;
  bank: string;
  accountNumber: string;
  icon: React.ElementType;
  color: string;
  nickname?: string;
  isFavorite?: boolean;
}
const ALL_Payees: Recipient[] = [{
  id: '1',
  name: 'Ahmed Khan',
  bank: 'Bank of Punjab',
  accountNumber: '1234567890123456',
  icon: BuildingIcon,
  color: '#F37024',
  nickname: 'Brother Ahmed ',
  isFavorite: false
}, {
  id: '2',
  name: 'Sara Ali',
  bank: 'Bank of Punjab',
  accountNumber: '9876543210987654',
  icon: BuildingIcon,
  color: '#F37024',
  nickname: 'Sister Sara',
  isFavorite: false
}, {
  id: '3',
  name: 'Bilal Ahmed',
  bank: 'Jazz Cash',
  accountNumber: '03001234567',
  icon: SmartphoneIcon,
  color: '#F37024',
  nickname: 'Billu Friend',
  isFavorite: false
}, {
  id: '4',
  name: 'Fatima Zahra',
  bank: 'Easypaisa',
  accountNumber: '03211234567',
  icon: WalletIcon,
  color: '#F37024',
  nickname: 'clothing Business',
  isFavorite: false
}];
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  })
};
const BANKS = ['Bank of Punjab', 'HBL', 'Meezan Bank', 'Bank Alfalah', 'Faysal Bank', 'JS Bank', 'UBL', 'MCB', 'Allied Bank', 'Standard Chartered', 'Jazz Cash', 'Easypaisa'];
interface PastPayment {
  amount: number;
  date: string;
  status: 'Success' | 'Failed';
}
const PAST_PAYMENTS: Record<string, PastPayment[]> = {
  '1': [{
    amount: 25000,
    date: '12 May 2026',
    status: 'Success'
  }, {
    amount: 10000,
    date: '28 Apr 2026',
    status: 'Success'
  }, {
    amount: 5000,
    date: '15 Apr 2026',
    status: 'Failed'
  }],
  '2': [{
    amount: 15000,
    date: '08 May 2026',
    status: 'Success'
  }, {
    amount: 7500,
    date: '22 Apr 2026',
    status: 'Success'
  }],
  '3': [{
    amount: 3000,
    date: '02 May 2026',
    status: 'Success'
  }],
  '4': []
};
let biometricCounter = 0;
// ── Shared CTA button style ────────────────────────────────────────────────
// height: 55px | width: 280px | border-radius: 8px | color: #F37024
const ctaStyle: React.CSSProperties = {
  height: '55px',
  width: '280px',
  borderRadius: '8px',
  background: '#F37024',
  color: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: '16px',
  border: 'none',
  cursor: 'pointer',
  transition: 'opacity 0.15s',
  margin: '0 auto'
};
export function SendMoneyScreen({
  onBack,
  onNavigateHome
}: SendMoneyScreenProps) {
  const {
    isDark
  } = useTheme();
  const [step, setStep] = useState<Step>('select_recipient');
  const [direction, setDirection] = useState(1);
  const [showNotifications, setShowNotifications] = useState(false);
  const [Payees, setPayees] = useState<Recipient[]>(ALL_Payees);
  const [editingPayeeId, setEditingPayeeId] = useState<string | null>(null);
  const [editingNickname, setEditingNickname] = useState('');
  const [deleteCandidate, setDeleteCandidate] = useState<Recipient | null>(null);
  const [PayeeToast, setPayeeToast] = useState<string | null>(null);
  const [expandedPayee, setExpandedPayee] = useState<string | null>(null);
  const confirmDeletePayee = () => {
    if (!deleteCandidate) return;
    setPayees(prev => prev.filter(b => b.id !== deleteCandidate.id));
    setDeleteCandidate(null);
    setPayeeToast('Payee removed successfully');
  };
  useEffect(() => {
    if (PayeeToast) {
      const t = setTimeout(() => setPayeeToast(null), 2500);
      return () => clearTimeout(t);
    }
  }, [PayeeToast]);
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(null);
  const [selectedQuickBank, setSelectedQuickBank] = useState<string>('');
  const [bankPayeesSearch, setBankPayeesSearch] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [purpose, setPurpose] = useState('Other');
  const [showPurposeDropdown, setShowPurposeDropdown] = useState(false);
  const [mpin, setMpin] = useState('');
  const [biometricPhase, setBiometricPhase] = useState<'scanning' | 'verified' | 'entering_mpin'>('scanning');
  const [scanType, setScanType] = useState<'face' | 'thumb'>('face');
  const PURPOSES = ['Family Support', 'Bill Payment', 'Salary', 'Business Payment', 'Loan Repayment', 'Gift', 'Other'];
  const [newPayeeName, setNewPayeeName] = useState('');
  const [newPayeeAccount, setNewPayeeAccount] = useState('');
  const [accountInputType, setAccountInputType] = useState<'account' | 'iban'>('account');
  const [newPayeeRelationship, setNewPayeeRelationship] = useState('');
  const [newPayeeBank, setNewPayeeBank] = useState('');
  const [newPayeeNickname, setNewPayeeNickname] = useState('');
  const [bankSearch, setBankSearch] = useState('');
  const [addPayeePhase, setAddPayeePhase] = useState<'entry' | 'fetching' | 'details_fetched' | 'preparing_payment'>('entry');
  const [savePayee, setSavePayee] = useState(false);
  const [nicknameError, setNicknameError] = useState('');
  const [cameFromQuickLink, setCameFromQuickLink] = useState(false);
  const [cameFromSavedPayee, setCameFromSavedPayee] = useState(false);
  const [isOwnAccountTransfer, setIsOwnAccountTransfer] = useState(false);
  const [showBopInfo, setShowBopInfo] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [transferMethod, setTransferMethod] = useState<'raast' | '1link'>('raast');
  const [paymentTab, setPaymentTab] = useState<'raast' | 'other'>('raast');
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(true);
  const [showAccountInfo, setShowAccountInfo] = useState(false);
  const [selectedBankForPayment, setSelectedBankForPayment] = useState<string>('');
  const PAY_FROM_ACCOUNTS = [{
    id: 'current',
    holder: 'Kashish Shewani',
    label: 'Current Account',
    number: '......0123 4567 8910',
    balance: 782354,
    isCard: false
  }, {
    id: 'savings',
    holder: 'Kashish Shewani',
    label: 'Savings Account',
    number: '......0456 7890 1234',
    balance: 145200,
    isCard: false
  }, {
    id: 'credit',
    holder: 'Kashish Shewani',
    label: 'Credit Card',
    number: '1234 5678 1234',
    balance: 0,
    isCard: true
  }];
  const [payFromAccount, setPayFromAccount] = useState(PAY_FROM_ACCOUNTS[0]);
  const [showPayFromDropdown, setShowPayFromDropdown] = useState(false);
  const OWN_ACCOUNTS = PAY_FROM_ACCOUNTS.filter(a => !a.isCard);
  const [ownReceivingAccount, setOwnReceivingAccount] = useState(OWN_ACCOUNTS[1]);
  const [showOwnReceivingDropdown, setShowOwnReceivingDropdown] = useState(false);
  const sameAccountError = isOwnAccountTransfer && payFromAccount.id === ownReceivingAccount.id;
  const renderBankLogo = (bank: string, size: 'sm' | 'md' = 'md') => {
    const cls = size === 'sm' ? 'w-6 h-6' : 'w-full h-full';
    if (bank === 'Bank of Punjab' || bank === 'The Bank of Punjab') {
      return <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="BOP" className={`${cls} object-contain`} data-id="element-2765" />;
    } else if (bank === 'HBL') {
      return <div className={`${cls} rounded-full bg-[#008B8B] flex items-center justify-center`} data-id="element-2766">
          <span className="text-white font-bold text-[10px]" data-id="element-2767">HBL</span>
        </div>;
    } else if (bank === 'Meezan Bank') {
      return <svg viewBox="0 0 44 44" className={cls} data-id="element-2768">
          <circle cx="22" cy="22" r="20" fill="#7C3AED" stroke="#22C55E" strokeWidth="2" data-id="element-2769" />
          <path d="M22 12L26 20H18L22 12Z M18 24H26L22 32L18 24Z" fill="white" data-id="element-2770" />
        </svg>;
    } else if (bank === 'Bank Alfalah') {
      return <svg viewBox="0 0 44 44" className={cls} data-id="element-2771">
          <circle cx="22" cy="22" r="20" fill="white" data-id="element-2772" />
          <path d="M22 10L28 22L22 18L16 22L22 10Z" fill="#DC2626" data-id="element-2773" />
        </svg>;
    } else if (bank === 'Faysal Bank') {
      return <div className={`${cls} rounded-full bg-[#FBBF24] flex items-center justify-center`} data-id="element-2774">
          <span className="text-[#1A1A1A] font-bold text-[8px]" data-id="element-2775">FAYSAL</span>
        </div>;
    } else if (bank === 'JS Bank') {
      return <svg viewBox="0 0 44 44" className={cls} data-id="element-2776">
          <circle cx="22" cy="22" r="20" fill="white" data-id="element-2777" />
          <text x="22" y="26" textAnchor="middle" className="text-[12px] font-bold" fill="#0EA5E9" data-id="element-2778">
            JS
          </text>
        </svg>;
    } else if (bank === 'Jazz Cash') {
      return <svg viewBox="0 0 40 40" className={cls} fill="none" data-id="element-2779">
          <path d="M8 20C8 14 12 8 20 8C24 8 27 10 29 13L25 17C23.5 15 21.8 14 20 14C15.6 14 13 17 13 20C13 23 15.6 26 20 26C21.8 26 23.5 25 25 23L29 27C27 30 24 32 20 32C12 32 8 26 8 20Z" fill="#DC2626" data-id="element-2780" />
          <path d="M28 12C30 14 32 17 32 20C32 23 30 26 28 28C26 26 24 23 24 20C24 17 26 14 28 12Z" fill="#FBBF24" data-id="element-2781" />
        </svg>;
    } else if (bank === 'Easypaisa') {
      return <svg viewBox="0 0 40 40" className={cls} fill="none" data-id="element-2782">
          <circle cx="20" cy="20" r="14" stroke="#22C55E" strokeWidth="3" fill="none" data-id="element-2783" />
          <path d="M12 20C12 15.6 15.6 12 20 12" stroke="#1A1A1A" strokeWidth="3" strokeLinecap="round" data-id="element-2784" />
        </svg>;
    } else {
      const colors = ['#0EA5E9', '#22C55E', '#F37024', '#7C3AED', '#EF4444'];
      const colorIndex = bank.charCodeAt(0) % colors.length;
      return <div className={`${cls} rounded-full flex items-center justify-center`} style={{
        backgroundColor: colors[colorIndex]
      }} data-id="element-2785">
          <span className="text-white font-bold text-[10px]" data-id="element-2786">
            {bank.split(' ').map(w => w[0]).join('').slice(0, 2)}
          </span>
        </div>;
    }
  };
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  const handleSelectRecipient = (recipient: Recipient) => {
    setSelectedRecipient(recipient);
    setNewPayeeBank(recipient.bank || '');
    setNewPayeeAccount(recipient.accountNumber || '');
    setNewPayeeName(recipient.name || '');
    setSelectedBankForPayment(recipient.bank || '');
    setCameFromSavedPayee(false);
    setIsOwnAccountTransfer(false);
    setAmount('');
    if (recipient.bank === 'Bank of Punjab') {
      setPaymentTab('raast');
    } else {
      setPaymentTab('other');
    }
    navigateTo('add_Payee');
  };
  useEffect(() => {
    if (step === 'processing') {
      const timer = setTimeout(() => navigateTo('success'), 3000);
      return () => clearTimeout(timer);
    }
  }, [step]);
  useEffect(() => {
    if (step === 'mpin') {
      setMpin('');
      setBiometricPhase('scanning');
      const currentScanType = biometricCounter % 2 === 0 ? 'face' : 'thumb';
      setScanType(currentScanType);
      biometricCounter++;
      const timeout1 = setTimeout(() => {
        setBiometricPhase('verified');
        const timeout2 = setTimeout(() => navigateTo('processing'), 800);
        return () => clearTimeout(timeout2);
      }, 2000);
      return () => clearTimeout(timeout1);
    }
  }, [step]);
  useEffect(() => {
    if (step === 'verify_Payee') {
      const t = setTimeout(() => navigateTo('Payee_saved'), 3000);
      return () => clearTimeout(t);
    }
  }, [step]);
  useEffect(() => {
    if (step === 'review') {
      if (isOwnAccountTransfer) {
        setPurpose('Transfer to own account');
      } else {
        setPurpose(prev => prev === 'Transfer to own account' ? 'Other' : prev);
      }
    }
  }, [step]);
  const renderStep = () => {
    switch (step) {
      // ─── SELECT RECIPIENT ────────────────────────────────────────────────────
      case 'select_recipient':
        return <motion.div key="select_recipient" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-24 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2787">
            <div className={`px-6 pt-6 pb-4 flex items-center justify-between ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2788">
              <button onClick={onBack} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} aria-label="Back" data-id="element-2789">
                <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-2790" />
                <span className={`text-[16px] font-regular ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2791">
                  Send Money
                </span>
              </button>
              <div className="flex-1" data-id="element-2792" />
              <button onClick={onNavigateHome} className={`p-2 -mr-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-2793">
                <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2794" />
              </button>
            </div>
            <div className="px-6 mt-6 space-y-6" data-id="element-2795">
              <div data-id="element-2796">
                <h3 className={`text-[13px] font-medium tracking-wider mb-4 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2797">
                  Quick Links
                </h3>
                <div className="flex gap-4 items-start" data-id="element-2798">
                  <button onClick={() => {
                  setNewPayeeBank('Bank of Punjab');
                  setSelectedQuickBank('Bank of Punjab');
                  setNewPayeeAccount('');
                  setNewPayeeName('');
                  setAccountInputType('account');
                  setAddPayeePhase('entry');
                  setCameFromQuickLink(true);
                  setCameFromSavedPayee(false);
                  setIsOwnAccountTransfer(false);
                  setSelectedBankForPayment('Bank of Punjab');
                  navigateTo('add_Payee');
                }} className="flex flex-col items-center gap-2 min-w-[68px]" data-id="element-2799">
                    <div className={`w-[68px] h-[68px] rounded-full flex items-center justify-center shadow-sm border ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.15)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2800">
                      <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="BOP" className="w-10 h-10 object-contain" data-id="element-2801" />
                    </div>
                    <span className={`text-[12px] font-medium ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2802">
                      BOP
                    </span>
                  </button>
                  <button onClick={() => {
                  setNewPayeeBank('Jazz Cash');
                  setSelectedQuickBank('Jazz Cash');
                  setNewPayeeAccount('');
                  setNewPayeeName('');
                  setAccountInputType('account');
                  setAddPayeePhase('entry');
                  setCameFromQuickLink(true);
                  setCameFromSavedPayee(false);
                  setIsOwnAccountTransfer(false);
                  setSelectedBankForPayment('Jazz Cash');
                  navigateTo('add_Payee');
                }} className="flex flex-col items-center gap-2 min-w-[68px]" data-id="element-2803">
                    <div className={`w-[68px] h-[68px] rounded-full flex items-center justify-center shadow-sm border ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.15)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2804">
                      <svg viewBox="0 0 40 40" className="w-9 h-9" fill="none" data-id="element-2805">
                        <path d="M8 20C8 14 12 8 20 8C24 8 27 10 29 13L25 17C23.5 15 21.8 14 20 14C15.6 14 13 17 13 20C13 23 15.6 26 20 26C21.8 26 23.5 25 25 23L29 27C27 30 24 32 20 32C12 32 8 26 8 20Z" fill="#DC2626" data-id="element-2806" />
                        <path d="M28 12C30 14 32 17 32 20C32 23 30 26 28 28C26 26 24 23 24 20C24 17 26 14 28 12Z" fill="#FBBF24" data-id="element-2807" />
                      </svg>
                    </div>
                    <span className={`text-[12px] font-medium ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2808">
                      JazzCash
                    </span>
                  </button>
                  <button onClick={() => {
                  setNewPayeeBank('Easypaisa');
                  setSelectedQuickBank('Easypaisa');
                  setNewPayeeAccount('');
                  setNewPayeeName('');
                  setAccountInputType('account');
                  setAddPayeePhase('entry');
                  setCameFromQuickLink(true);
                  setCameFromSavedPayee(false);
                  setIsOwnAccountTransfer(false);
                  setSelectedBankForPayment('Easypaisa');
                  navigateTo('add_Payee');
                }} className="flex flex-col items-center gap-2 min-w-[68px]" data-id="element-2809">
                    <div className={`w-[68px] h-[68px] rounded-full flex items-center justify-center shadow-sm border ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.15)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2810">
                      <svg viewBox="0 0 40 40" className="w-9 h-9" fill="none" data-id="element-2811">
                        <circle cx="20" cy="20" r="14" stroke="#22C55E" strokeWidth="3" fill="none" data-id="element-2812" />
                        <path d="M12 20C12 15.6 15.6 12 20 12" stroke="#1A1A1A" strokeWidth="3" strokeLinecap="round" data-id="element-2813" />
                      </svg>
                    </div>
                    <span className={`text-[12px] font-medium ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2814">
                      EasyPaisa
                    </span>
                  </button>
                  <button onClick={() => {
                  setNewPayeeBank('');
                  setNewPayeeAccount('');
                  setNewPayeeName('');
                  setAccountInputType('account');
                  setAddPayeePhase('entry');
                  setCameFromQuickLink(false);
                  setCameFromSavedPayee(false);
                  setIsOwnAccountTransfer(false);
                  setSelectedBankForPayment('');
                  navigateTo('add_Payee');
                }} className="flex flex-col items-center gap-2 min-w-[68px]" data-id="element-2815">
                    <div className="w-[68px] h-[68px] rounded-full bg-[#F37024] flex items-center justify-center shadow-md" data-id="element-2816">
                      <PlusIcon className="w-8 h-8 text-white" strokeWidth={2} data-id="element-2817" />
                    </div>
                    <span className={`text-[12px] font-medium ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2818">
                      New Transfer
                    </span>
                  </button>
                </div>
              </div>
              <div data-id="element-2819">
                <input type="text" placeholder="Search name, bank or account numb" className={`w-full rounded-2xl py-4 px-4 text-[15px] outline-none border ${isDark ? 'bg-[#3D2518] text-white placeholder:text-[#B8A89E]/70 border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#303030] placeholder:text-[#818285] border-[#F0E8E3]'}`} data-id="element-2820" />
              </div>
              <button onClick={() => {
              setNewPayeeBank('Bank of Punjab');
              setNewPayeeAccount('');
              setNewPayeeName('Own Account');
              setAccountInputType('account');
              setAddPayeePhase('entry');
              setCameFromQuickLink(false);
              setCameFromSavedPayee(false);
              setIsOwnAccountTransfer(true);
              setOwnReceivingAccount(OWN_ACCOUNTS[1]);
              setPayFromAccount(PAY_FROM_ACCOUNTS[0]);
              setSelectedBankForPayment('');
              navigateTo('add_Payee');
            }} className="w-full rounded-xl py-4 px-4 text-center text-[15px] font-regular border-2 transition-all active:scale-[0.98]" style={{
              background: isDark ? 'transparent' : '#FFFFFF',
              borderColor: '#F37024',
              color: '#F37024'
            }} data-id="element-2821">
                Transfer to own Accounts
              </button>
              <div data-id="element-2822">
                <h3 className={cn(`text-[13px] font-medium tracking-wider mb-4 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`, 'font-[500]')} data-id="element-2823">
                  All Payees
                </h3>
                <div className="space-y-3" data-id="element-2824">
                  {Payees.map(rec => {
                  const Icon = rec.icon;
                  const pastPayments = PAST_PAYMENTS[rec.id] || [];
                  const isExpanded = expandedPayee === rec.id;
                  return <motion.div key={rec.id} layout className={`w-full rounded-2xl shadow-sm overflow-hidden ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white border border-[#F0E8E3]'}`} data-id="element-2825">
                        <button onClick={() => handleSelectRecipient(rec)} className="w-full flex items-center gap-4 text-left p-4" data-id="element-2826">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2827">
                            <Icon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-2828" />
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col justify-center" data-id="element-2829">
                            <h4 className={`text-[16px] font-bold truncate ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} style={{
                          fontFamily: 'Poppins, sans-serif'
                        }} data-id="element-2830">
                              {rec.nickname}
                            </h4>
                            <p className="text-[13px] font-medium truncate text-[#5A5B5E]" style={{
                          fontFamily: 'Poppins, sans-serif'
                        }} data-id="element-2831">
                              {rec.name}
                            </p>
                            <p className="text-[12px] truncate text-[#818285]" style={{
                          fontFamily: 'Poppins, sans-serif'
                        }} data-id="element-2832">
                              {rec.accountNumber}
                            </p>
                          </div>
                          <ChevronRightIcon className={`w-5 h-5 shrink-0 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2833" />
                        </button>
                        <div className={`mx-4 h-px ${isDark ? 'bg-[rgba(243,112,36,0.1)]' : 'bg-[#F0E8E3]'}`} data-id="element-2834" />
                        <button onClick={() => setExpandedPayee(isExpanded ? null : rec.id)} className={`w-full flex items-center justify-end gap-1.5 px-4 py-2.5 transition-colors ${isDark ? 'hover:bg-[#3D2518]/50' : 'hover:bg-[#FFF7F0]'}`} data-id="element-2835">
                          <span className={`text-[12px] font-medium ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2836">
                            Past Payments
                          </span>
                          <motion.div animate={{
                        rotate: isExpanded ? 180 : 0
                      }} transition={{
                        duration: 0.2
                      }} data-id="element-2837">
                            <ChevronDownIcon className={`w-4 h-4 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2838" />
                          </motion.div>
                        </button>
                        <AnimatePresence initial={false} data-id="element-2839">
                          {isExpanded && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: 'easeOut'
                      }} className="overflow-hidden" data-id="element-2840">
                              <div className={`mx-4 mb-1 h-px ${isDark ? 'bg-[rgba(243,112,36,0.08)]' : 'bg-[#F5EDEA]'}`} data-id="element-2841" />
                              {pastPayments.length === 0 ? <div className="px-4 py-4 text-center" data-id="element-2842">
                                  <p className={`text-[12px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2843">
                                    No past payments found
                                  </p>
                                </div> : <div className="px-4 pb-3 pt-1 space-y-0" data-id="element-2844">
                                  {pastPayments.map((txn, idx) => <div key={idx} data-id="element-2845">
                                      <div className="flex items-center justify-between py-2.5" data-id="element-2846">
                                        <div className="flex items-center gap-3" data-id="element-2847">
                                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${txn.status === 'Success' ? 'bg-[#22C55E]/10' : 'bg-[#EF4444]/10'}`} data-id="element-2848">
                                            {txn.status === 'Success' ? <CheckCircleIcon className="w-4 h-4 text-[#22C55E]" strokeWidth={2} data-id="element-2849" /> : <XIcon className="w-4 h-4 text-[#EF4444]" strokeWidth={2.5} data-id="element-2850" />}
                                          </div>
                                          <div data-id="element-2851">
                                            <p className={`text-[12px] font-semibold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2852">
                                              PKR{' '}
                                              {txn.amount.toLocaleString('en-US')}
                                            </p>
                                            <p className={`text-[11px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2853">
                                              {txn.date}
                                            </p>
                                          </div>
                                        </div>
                                        <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${txn.status === 'Success' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`} data-id="element-2854">
                                          {txn.status}
                                        </span>
                                      </div>
                                      {idx < pastPayments.length - 1 && <div className={`h-px ${isDark ? 'bg-[rgba(243,112,36,0.06)]' : 'bg-[#F5EDEA]'}`} data-id="element-2855" />}
                                    </div>)}
                                </div>}
                            </motion.div>}
                        </AnimatePresence>
                      </motion.div>;
                })}
                </div>
              </div>
            </div>
            <AnimatePresence data-id="element-2856">
              {deleteCandidate && <>
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} onClick={() => setDeleteCandidate(null)} className="fixed inset-0 bg-black/50 z-[110] backdrop-blur-sm" data-id="element-2857" />
                  <motion.div initial={{
                opacity: 0,
                scale: 0.9,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.9,
                y: 20
              }} transition={{
                type: 'spring',
                damping: 22,
                stiffness: 300
              }} className={`fixed inset-x-6 top-1/2 -translate-y-1/2 z-[111] max-w-[360px] mx-auto rounded-3xl p-6 shadow-2xl ${isDark ? 'bg-[#2D1810]' : 'bg-white'}`} data-id="element-2858">
                    <div className="w-14 h-14 rounded-full bg-[#EF4444]/10 flex items-center justify-center mx-auto mb-4" data-id="element-2859">
                      <AlertTriangleIcon className="w-7 h-7 text-[#EF4444]" strokeWidth={2} data-id="element-2860" />
                    </div>
                    <h3 className={`text-[18px] font-bold text-center mb-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2861">
                      Delete Payee?
                    </h3>
                    <p className={`text-[14px] text-center mb-6 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2862">
                      Are you sure you want to delete{' '}
                      <span className={`font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2863">
                        {deleteCandidate.name}
                      </span>
                      ? This action cannot be undone.
                    </p>
                    <div className="space-y-3 flex flex-col items-center" data-id="element-2864">
                      {/* ── Delete confirm CTA ── */}
                      <button onClick={confirmDeletePayee} style={{
                    ...ctaStyle,
                    background: '#EF4444'
                  }} data-id="element-2865">
                        Yes, Delete
                      </button>
                      <button onClick={() => setDeleteCandidate(null)} style={{
                    ...ctaStyle,
                    background: isDark ? 'rgba(255,255,255,0.05)' : '#F0E8E3',
                    color: isDark ? '#FFFFFF' : '#303030'
                  }} data-id="element-2866">
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                </>}
            </AnimatePresence>
            <AnimatePresence data-id="element-2867">
              {PayeeToast && <motion.div initial={{
              opacity: 0,
              y: 40
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 40
            }} className="fixed inset-x-6 bottom-8 z-[112] max-w-[360px] mx-auto" data-id="element-2868">
                  <div className="flex items-center gap-3 px-4 py-3.5 rounded-2xl shadow-lg bg-[#22C55E] text-white" data-id="element-2869">
                    <CheckCircleIcon className="w-5 h-5 shrink-0" strokeWidth={2} data-id="element-2870" />
                    <p className="text-[14px] font-semibold" data-id="element-2871">{PayeeToast}</p>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>;
      // ─── SELECT BANK ─────────────────────────────────────────────────────────
      case 'select_bank':
        const sortedBanks = (() => {
          const bop = BANKS.filter(b => b === 'Bank of Punjab');
          const rest = BANKS.filter(b => b !== 'Bank of Punjab').sort((a, b) => a.localeCompare(b));
          return [...bop, ...rest];
        })();
        const filteredBanks = sortedBanks.filter(bank => bank.toLowerCase().includes(bankSearch.toLowerCase()));
        return <motion.div key="select_bank" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2872">
            <div className={`px-6 pt-6 pb-4 flex items-center justify-between ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2873">
              <button onClick={() => {
              setBankSearch('');
              navigateTo('add_Payee', -1);
            }} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} aria-label="Back" data-id="element-2874">
                <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-2875" />
                <span className={`text-[16px] font-regular ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2876">
                  Select Bank
                </span>
              </button>
              <div className="flex-1" data-id="element-2877" />
              <button onClick={onNavigateHome} className={`p-2 -mr-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-2878">
                <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2879" />
              </button>
            </div>
            <div className="px-6 mt-6 flex-1 flex flex-col pb-8" data-id="element-2880">
              <div className="mb-6" data-id="element-2881">
                <input type="text" value={bankSearch} onChange={e => setBankSearch(e.target.value)} placeholder="Search bank name" autoFocus className={`w-full rounded-2xl py-4 px-4 text-[15px] outline-none border ${isDark ? 'bg-[#3D2518] text-white placeholder:text-[#B8A89E]/70 border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#303030] placeholder:text-[#818285] border-[#F0E8E3]'}`} data-id="element-2882" />
              </div>
              <div className="space-y-3 flex-1 overflow-y-auto" data-id="element-2883">
                {filteredBanks.map(bank => <button key={bank} onClick={() => {
                setSelectedBankForPayment(bank);
                setNewPayeeBank(bank);
                setBankSearch('');
                navigateTo('add_Payee', -1);
              }} className={`w-full rounded-2xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow active:scale-[0.98] ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white border border-[#F0E8E3]'}`} data-id="element-2884">
                    <div className="w-11 h-11 shrink-0" data-id="element-2885">
                      {renderBankLogo(bank)}
                    </div>
                    <span className={`text-[15px] font-semibold flex-1 text-left ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2886">
                      {bank}
                    </span>
                    <ChevronRightIcon className={`w-5 h-5 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2887" />
                  </button>)}
                {filteredBanks.length === 0 && <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-2888">
                    <SearchIcon className={`w-10 h-10 mb-3 ${isDark ? 'text-[#B8A89E]/40' : 'text-[#818285]/40'}`} data-id="element-2889" />
                    <p className={`text-[15px] font-medium ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2890">
                      No banks found
                    </p>
                  </div>}
              </div>
            </div>
          </motion.div>;
      // ─── ADD Payee (PAYMENT DETAILS) ────────────────────────────────────────
      case 'add_Payee':
        return <motion.div key="add_Payee" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2891">
            <div className={`px-6 pt-6 pb-4 flex items-center justify-between ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2892">
              <button onClick={() => {
              setCameFromSavedPayee(false);
              setCameFromQuickLink(false);
              setIsOwnAccountTransfer(false);
              navigateTo('select_recipient', -1);
            }} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} aria-label="Back" data-id="element-2893">
                <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-2894" />
                <span className={`text-[16px] font-regular ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2895">
                  Payment Details
                </span>
              </button>
              <div className="flex-1" data-id="element-2896" />
              <button onClick={onNavigateHome} className={`p-2 -mr-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-2897">
                <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2898" />
              </button>
            </div>
            <div className="px-6 mt-2 flex-1 flex flex-col pb-8" data-id="element-2899">
              <div className="space-y-5" data-id="element-2900">
                <div data-id="element-2901">
                  <p className={cn(`text-[13px] font-bold mb-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`, 'font-[500]')} data-id="element-2902">
                    {isOwnAccountTransfer ? 'Pay To' : 'Account Number / IBAN'}
                  </p>
                  {isOwnAccountTransfer ? <div className="relative" data-id="element-2903">
                      <button type="button" onClick={() => setShowOwnReceivingDropdown(!showOwnReceivingDropdown)} className={`w-full rounded-xl px-4 py-3.5 flex items-center justify-between border ${isDark ? 'bg-[#3D2518] border-[rgba(243,112,36,0.1)]' : 'bg-white border-[#E5E5E5]'} ${sameAccountError ? 'border-red-400' : ''}`} data-id="element-2904">
                        <div className="text-left min-w-0" data-id="element-2905">
                          <p className={`text-[14px] font-bold truncate ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2906">
                            {ownReceivingAccount.holder}
                          </p>
                          <p className={`text-[12px] truncate ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2907">
                            {ownReceivingAccount.label}{' '}
                            <span data-id="element-2908">{ownReceivingAccount.number}</span>
                          </p>
                        </div>
                        <ChevronDownIcon className={`w-5 h-5 transition-transform ${showOwnReceivingDropdown ? 'rotate-180' : ''} ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2909" />
                      </button>
                      <AnimatePresence data-id="element-2910">
                        {showOwnReceivingDropdown && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} className={`absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden shadow-lg z-30 ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white border border-[#E5E5E5]'}`} data-id="element-2911">
                            {OWN_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
                        setOwnReceivingAccount(acc);
                        setShowOwnReceivingDropdown(false);
                      }} className={`w-full text-left p-4 flex items-center justify-between transition-colors ${isDark ? 'hover:bg-[#3D2518]' : 'hover:bg-[#FFF7F0]'} ${ownReceivingAccount.id === acc.id ? isDark ? 'bg-[#3D2518]' : 'bg-[#FFF7F0]' : ''}`} data-id="element-2912">
                                <div className="min-w-0" data-id="element-2913">
                                  <p className={`text-[14px] font-bold truncate ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2914">
                                    {acc.holder}
                                  </p>
                                  <p className={`text-[12px] truncate ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2915">
                                    {acc.label} {acc.number}
                                  </p>
                                </div>
                                {ownReceivingAccount.id === acc.id && <CheckIcon className="w-5 h-5 text-[#F37024] shrink-0" strokeWidth={2.5} data-id="element-2916" />}
                              </button>)}
                          </motion.div>}
                      </AnimatePresence>
                      {sameAccountError && <p className="text-red-500 text-[11px] mt-1.5 ml-1" data-id="element-2917">
                          Source and destination accounts cannot be the same.
                        </p>}
                    </div> : <div className="relative" data-id="element-2918">
                      <input type="text" inputMode="text" value={newPayeeAccount} onFocus={() => setShowAccountInfo(false)} onChange={e => {
                    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 24);
                    setNewPayeeAccount(val);
                    if (val.toLowerCase().startsWith('pkbpun')) {
                      setSelectedBankForPayment('Bank of Punjab');
                      setNewPayeeBank('Bank of Punjab');
                    }
                  }} maxLength={24} placeholder="Enter details" className={`w-full rounded-xl py-4 pl-4 pr-10 text-[15px] outline-none border ${isDark ? 'bg-[#3D2518] text-white placeholder:text-[#B8A89E]/70 border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#303030] placeholder:text-[#818285] border-[#E5E5E5]'}`} data-id="element-2919" />
                      <button type="button" onMouseDown={e => {
                    e.preventDefault();
                    setShowAccountInfo(!showAccountInfo);
                  }} className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-2920">
                        <InfoIcon className={`w-4 h-4 ${isDark ? 'text-[#B8A89E]' : 'text-[#9E9E9E]'}`} strokeWidth={1.8} data-id="element-2921" />
                      </button>
                      <AnimatePresence data-id="element-2922">
                        {showAccountInfo && <motion.div initial={{
                      opacity: 0,
                      y: -4,
                      scale: 0.95
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1
                    }} exit={{
                      opacity: 0,
                      y: -4,
                      scale: 0.95
                    }} transition={{
                      duration: 0.15
                    }} className="absolute right-0 top-full mt-2 z-50" data-id="element-2923">
                            <div className={`px-3 py-2 rounded-lg shadow-lg text-[11px] font-medium whitespace-normal max-w-[220px] leading-relaxed ${isDark ? 'bg-[#3D2518] text-white border border-[rgba(243,112,36,0.2)]' : 'bg-[#1A1A1A] text-white'}`} data-id="element-2924">
                              IBAN is 24 characters. A Pakistani IBAN is exactly
                              24 characters long, starting with PK, followed by
                              2 check digits, a 4-digit bank identifier, and
                              your 16-digit account number.
                              <div className={`absolute -top-1.5 right-3 w-3 h-3 rotate-45 ${isDark ? 'bg-[#3D2518]' : 'bg-[#1A1A1A]'}`} data-id="element-2925" />
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>}
                </div>
                {!isOwnAccountTransfer && <div data-id="element-2926">
                    <p className={`text-[13px] font-medium mb-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2927">
                      Bank
                    </p>
                    <button onClick={() => navigateTo('select_bank')} className={`w-full rounded-xl py-3.5 px-4 flex items-center justify-between border ${isDark ? 'bg-[#3D2518] border-[rgba(243,112,36,0.1)]' : 'bg-white border-[#E5E5E5]'}`} data-id="element-2928">
                      <div className="flex items-center gap-3" data-id="element-2929">
                        {selectedBankForPayment ? <div className="w-8 h-8 shrink-0" data-id="element-2930">
                            {renderBankLogo(selectedBankForPayment)}
                          </div> : <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-[#2D1810]' : 'bg-[#F0F0F0]'}`} data-id="element-2931">
                            <BuildingIcon className={`w-4 h-4 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2932" />
                          </div>}
                        <span className={`text-[15px] ${selectedBankForPayment ? isDark ? 'text-white' : 'text-[#303030]' : isDark ? 'text-[#B8A89E]/70' : 'text-[#818285]'}`} data-id="element-2933">
                          {selectedBankForPayment || 'Select Bank'}
                        </span>
                      </div>
                      <ChevronRightIcon className={`w-5 h-5 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2934" />
                    </button>
                  </div>}
                <div data-id="element-2935">
                  <p className={`text-[13px] font-medium mb-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2936">
                    Pay From
                  </p>
                  <div className="relative" data-id="element-2937">
                    <button type="button" onClick={() => setShowPayFromDropdown(!showPayFromDropdown)} className={`w-full rounded-xl px-4 py-3.5 flex items-center justify-between border ${isDark ? 'bg-[#3D2518] border-[rgba(243,112,36,0.1)]' : 'bg-white border-[#E5E5E5]'} ${sameAccountError ? 'border-red-400' : ''}`} data-id="element-2938">
                      <div className="text-left min-w-0 flex items-center gap-3" data-id="element-2939">
                        {payFromAccount.isCard && <CreditCardIcon className="w-4 h-4 text-[#F37024] shrink-0" data-id="element-2940" />}
                        <div data-id="element-2941">
                          <p className={`text-[14px] font-bold truncate ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2942">
                            {payFromAccount.holder}
                          </p>
                          <p className={`text-[12px] truncate ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2943">
                            {payFromAccount.label}{' '}
                            <span data-id="element-2944">{payFromAccount.number}</span>
                          </p>
                        </div>
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 transition-transform ${showPayFromDropdown ? 'rotate-180' : ''} ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2945" />
                    </button>
                    <AnimatePresence data-id="element-2946">
                      {showPayFromDropdown && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} className={`absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden shadow-lg z-30 ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white border border-[#E5E5E5]'}`} data-id="element-2947">
                          {PAY_FROM_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
                        setPayFromAccount(acc);
                        setShowPayFromDropdown(false);
                      }} className={`w-full text-left p-4 flex items-center justify-between transition-colors ${isDark ? 'hover:bg-[#3D2518]' : 'hover:bg-[#FFF7F0]'} ${payFromAccount.id === acc.id ? isDark ? 'bg-[#3D2518]' : 'bg-[#FFF7F0]' : ''}`} data-id="element-2948">
                              <div className="flex items-center gap-3 min-w-0" data-id="element-2949">
                                {acc.isCard && <CreditCardIcon className="w-4 h-4 text-[#F37024] shrink-0" data-id="element-2950" />}
                                <div className="min-w-0" data-id="element-2951">
                                  <p className={`text-[14px] font-bold truncate ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2952">
                                    {acc.holder}
                                  </p>
                                  <p className={`text-[12px] truncate ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2953">
                                    {acc.label} {acc.number}
                                  </p>
                                </div>
                              </div>
                              {payFromAccount.id === acc.id && <CheckIcon className="w-5 h-5 text-[#F37024] shrink-0" strokeWidth={2.5} data-id="element-2954" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                    {sameAccountError && <p className="text-red-500 text-[11px] mt-1.5 ml-1" data-id="element-2955">
                        Source and destination accounts cannot be the same.
                      </p>}
                  </div>
                </div>
                {payFromAccount.isCard && <p className="text-[10px] mt-[0px]" style={{
                color: '#9E9E9E',
                marginTop: 0
              }} data-id="element-2956">
                    Cash Advance charges may apply
                  </p>}
                <div className="flex flex-col items-center mt-6 mb-2" data-id="element-2957">
                  <div className="flex items-baseline gap-2 justify-center w-full" data-id="element-2958">
                    <span className={`text-[20px] font-medium ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2959">
                      PKR
                    </span>
                    <input type="text" inputMode="numeric" value={amount ? Number(amount.replace(/,/g, '')).toLocaleString('en-US') : ''} onChange={e => {
                    const raw = e.target.value.replace(/[^\d]/g, '');
                    setAmount(raw);
                  }} placeholder="0" className={`text-[56px] font-medium bg-transparent outline-none text-center tracking-tight ${isDark ? 'text-white placeholder:text-white/30' : 'text-[#5A1408] placeholder:text-[#5A1408]/30'}`} style={{
                    width: amount ? `${Math.max(amount.length, 1) * 36 + 20}px` : '80px',
                    maxWidth: '100%',
                    minWidth: '80px'
                  }} data-id="element-2960" />
                  </div>
                  <div className={`w-[80%] max-w-[260px] h-px mt-1 ${isDark ? 'bg-[#3D2518]' : 'bg-[#E5E5E5]'}`} data-id="element-2961" />
                  {(() => {
                  const enteredAmount = Number(amount.replace(/,/g, '')) || 0;
                  const balance = payFromAccount.isCard ? 0 : payFromAccount.balance;
                  const remaining = balance - enteredAmount;
                  const insufficient = !payFromAccount.isCard && remaining < 0;
                  return <p className={`text-[13px] mt-3 ${isDark ? 'text-[#B8A89E]' : 'text-[#1A1A1A]'}`} data-id="element-2962">
                        {payFromAccount.isCard ? <span className={insufficient ? 'text-red-500' : 'text-[#818285]'} data-id="element-2963">
                            Credit Limit: PKR{' '}
                            <span className="text-[#F37024]" data-id="element-2964">50,000</span>
                            {'  |  '}Available: PKR{' '}
                            <span className={enteredAmount > 50000 ? 'text-red-500' : 'text-[#F37024]'} data-id="element-2965">
                              {(50000 - enteredAmount).toLocaleString('en-US')}
                            </span>
                          </span> : <>
                            Available Balance: PKR{' '}
                            <span className={insufficient ? 'text-red-500' : 'text-[#F37024]'} data-id="element-2966">
                              {remaining.toLocaleString('en-US')}
                            </span>
                          </>}
                      </p>;
                })()}
                </div>
                {!isOwnAccountTransfer && <div className="mt-2" data-id="element-2967">
                    <div className={`flex rounded-2xl overflow-hidden border ${isDark ? 'border-[rgba(243,112,36,0.15)] bg-[#2D1810]' : 'border-[#E5E5E5] bg-[#F5F5F5]'}`} data-id="element-2968">
                      <button onClick={() => setPaymentTab('raast')} className={`flex-1 flex items-center justify-center gap-2 py-3.5 transition-all ${paymentTab === 'raast' ? isDark ? 'bg-[#3D2518]' : 'bg-white shadow-sm' : ''}`} data-id="element-2969">
                        <div className={cn(`w-7 h-7 rounded flex items-center justify-center ${paymentTab === 'raast' ? 'bg-[#E8F5E9]' : isDark ? 'bg-[#2D1810]' : 'bg-[#EBEBEB]'}`, 'bg-[#ffffff]')} data-id="element-2970">
                          <img src="https://cdn.magicpatterns.com/uploads/1ZmoJtWAAdB82VYTszuaqM/pasted-image.svg" alt="Raast" className="w-5 h-5 object-contain" data-id="element-2971" />
                        </div>
                        <span className={`text-[14px] font-bold ${paymentTab === 'raast' ? isDark ? 'text-white' : 'text-[#1A1A1A]' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2972">
                          Raast
                        </span>
                      </button>
                      <div className={`w-px self-stretch ${isDark ? 'bg-[rgba(243,112,36,0.15)]' : 'bg-[#E5E5E5]'}`} data-id="element-2973" />
                      <button onClick={() => setPaymentTab('other')} className={`flex-1 flex items-center justify-center gap-2 py-3.5 transition-all ${paymentTab === 'other' ? isDark ? 'bg-[#3D2518]' : 'bg-white shadow-sm' : ''}`} data-id="element-2974">
                        <div className={`w-7 h-7 rounded flex items-center justify-center ${paymentTab === 'other' ? 'bg-[#FFF3E0]' : isDark ? 'bg-[#2D1810]' : 'bg-[#EBEBEB]'}`} data-id="element-2975">
                          <BuildingIcon className="w-4 h-4" style={{
                        color: paymentTab === 'other' ? '#F37024' : '#9E9E9E'
                      }} data-id="element-2976" />
                        </div>
                        <span className={`text-[14px] font-bold ${paymentTab === 'other' ? isDark ? 'text-white' : 'text-[#1A1A1A]' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2977">
                          FT / IBFT
                        </span>
                      </button>
                    </div>
                    {paymentTab === 'raast' ? <p className={`text-[12px] mt-1.5 ml-1 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2978">
                        Free of cost
                      </p> : <p className={`text-[12px] mt-1.5 ml-1 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2979">
                        Charges may apply
                      </p>}
                  </div>}
              </div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="mt-auto pt-8 flex justify-center" data-id="element-2980">
                {/* ── Continue CTA ── */}
                <button disabled={sameAccountError || !isOwnAccountTransfer && !newPayeeAccount.trim() || !isOwnAccountTransfer && !selectedBankForPayment.trim() || !amount || Number(amount.replace(/,/g, '')) <= 0 || (payFromAccount.isCard ? Number(amount.replace(/,/g, '')) > 50000 : Number(amount.replace(/,/g, '')) > payFromAccount.balance)} onClick={() => {
                const newPayee: Recipient = {
                  id: `temp-${Date.now()}`,
                  name: isOwnAccountTransfer ? ownReceivingAccount.label : newPayeeName || 'Recipient',
                  bank: isOwnAccountTransfer ? 'Bank of Punjab' : paymentTab === 'raast' ? 'RAAST' : newPayeeBank,
                  accountNumber: isOwnAccountTransfer ? ownReceivingAccount.number : newPayeeAccount,
                  icon: BuildingIcon,
                  color: '#F37024'
                };
                setSelectedRecipient(newPayee);
                setTransferMethod(paymentTab === 'raast' ? 'raast' : '1link');
                navigateTo('review');
              }} style={{
                ...ctaStyle,
                opacity: sameAccountError || !isOwnAccountTransfer && !newPayeeAccount.trim() || !isOwnAccountTransfer && !selectedBankForPayment.trim() || !amount || Number(amount.replace(/,/g, '')) <= 0 ? 0.5 : 1
              }} data-id="element-2981">
                  Continue
                </button>
              </motion.div>
            </div>
          </motion.div>;
      // ─── VERIFY Payee ───────────────────────────────────────────────────────
      case 'verify_Payee':
        return <motion.div key="verify_Payee" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2982">
            <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} className="mb-6" data-id="element-2983">
              <ShieldIcon className="w-16 h-16 text-[#F37024]" strokeWidth={1.5} data-id="element-2984" />
            </motion.div>
            <p className={`text-[20px] font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-2985">
              Verifying...
            </p>
            <p className={`text-[14px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2986">
              Saving Payee details
            </p>
          </motion.div>;
      // ─── Payee SAVED ─────────────────────────────────────────────────────────
      case 'Payee_saved':
        return <motion.div key="Payee_saved" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2987">
            <div className={`px-6 pt-6 pb-4 flex items-center justify-between sticky top-0 z-10 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2988">
              <div className="w-10" data-id="element-2989" />
              <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto" data-id="element-2990" />
              <div className="w-10" data-id="element-2991" />
            </div>
            <div className="px-6 flex-1 flex flex-col items-center justify-center pb-8" data-id="element-2992">
              <motion.div initial={{
              scale: 0.5,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              type: 'spring',
              damping: 15,
              stiffness: 200,
              delay: 0.1
            }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-6" data-id="element-2993">
                <CheckCircleIcon className="w-14 h-14 text-[#22C55E]" strokeWidth={1.5} data-id="element-2994" />
              </motion.div>
              <motion.h1 initial={{
              y: 10,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.25
            }} className={`text-[28px] font-bold text-center mb-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2995">
                Payee Added Successfully
              </motion.h1>
              <motion.p initial={{
              y: 10,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.35
            }} className={`text-[15px] text-center max-w-[280px] mb-4 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2996">
                <span className={`font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2997">
                  {selectedRecipient?.name}
                </span>{' '}
                has been saved to your Payees.
              </motion.p>
              <motion.div initial={{
              y: 10,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.55
            }} className="w-full flex justify-center mt-8" data-id="element-2998">
                {/* ── OK CTA ── */}
                <button onClick={() => {
                setNewPayeeName('');
                setNewPayeeAccount('');
                setNewPayeeRelationship('');
                setNewPayeeBank('');
                setNewPayeeNickname('');
                onNavigateHome?.();
              }} style={ctaStyle} data-id="element-2999">
                  OK
                </button>
              </motion.div>
            </div>
          </motion.div>;
      // ─── REVIEW ──────────────────────────────────────────────────────────────
      case 'review':
        return <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3000">
            <div className="px-5 pt-6 pb-4 flex items-center justify-between" data-id="element-3001">
              <button onClick={() => navigateTo('add_Payee', -1)} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} aria-label="Back" data-id="element-3002">
                <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-3003" />
                <span className={`text-[16px] font-regular ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3004">
                  Review
                </span>
              </button>
              <button onClick={onNavigateHome} className="p-2 -mr-2" aria-label="Home" data-id="element-3005">
                <HomeIcon className="w-6 h-6 text-[#5A1408]" strokeWidth={2} data-id="element-3006" />
              </button>
            </div>
            <div className="px-5 flex-1 flex flex-col pb-6 space-y-4" data-id="element-3007">
              <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35
            }} className="text-center pl-[20px] ml-[6px]" style={{
              width: '349px',
              height: '150px',
              borderRadius: '8px',
              padding: '0 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundImage: isDark ? 'linear-gradient(#2D1810, #2D1810), linear-gradient(to right, rgba(243,112,36,0.36) 0%, rgba(255,255,255,0.36) 18%, rgba(255,255,255,0.36) 59%, rgba(243,112,36,0.36) 100%)' : 'linear-gradient(#FFFFFF, #FFFFFF), linear-gradient(to right, rgba(243,112,36,0.36) 0%, rgba(255,255,255,0.36) 18%, rgba(255,255,255,0.36) 59%, rgba(243,112,36,0.36) 100%)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              border: '1px solid transparent',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 10px rgba(0,0,0,0.02)'
            }} data-id="element-3008">
                <div className="flex items-baseline justify-center gap-2" data-id="element-3009">
                  <span className="text-[20px] font-medium" style={{
                  color: isDark ? '#B8A89E' : '#818285'
                }} data-id="element-3010">
                    PKR
                  </span>
                  <span className="text-[48px] font-medium leading-none" style={{
                  color: isDark ? '#FFFFFF' : '#5A1408',
                  letterSpacing: '-1px'
                }} data-id="element-3011">
                    {amount ? Number(amount.replace(/,/g, '')).toLocaleString('en-US') : '0'}
                  </span>
                </div>
                <p className="text-[12px] mt-2 font-medium" style={{
                color: isDark ? '#B8A89E' : '#1A1A1A'
              }} data-id="element-3012">
                  Transaction fees: 0.00
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              delay: 0.05
            }} className="rounded-xl px-5 py-5 relative" style={{
              background: isDark ? '#2D1810' : '#FFFFFF',
              border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #F0E8E3',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 10px rgba(0,0,0,0.02)'
            }} data-id="element-3013">
                <div aria-hidden className="absolute" style={{
                left: 27,
                top: 36,
                bottom: 60,
                width: 0,
                borderLeft: '1px dashed #F37024'
              }} data-id="element-3014" />
                <div className="flex gap-4 pb-5" data-id="element-3015">
                  <div className="shrink-0 pt-1" data-id="element-3016">
                    <div className="w-4 h-4 rounded-full bg-[#F37024] border-[3px] border-white shadow-[0_0_0_1px_#F37024]" data-id="element-3017" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3018">
                    <div className="flex items-center justify-between mb-1" data-id="element-3019">
                      <p className="text-[14px] font-bold" style={{
                      color: '#5A1408'
                    }} data-id="element-3020">
                        Pay From
                      </p>
                      {!isOwnAccountTransfer && <span className="text-[10px] font-bold px-3 py-1 rounded-full bg-[#F5F5F5] text-[#1A1A1A]" data-id="element-3021">
                          {transferMethod === 'raast' ? 'RAAST' : 'IBFT'}
                        </span>}
                    </div>
                    <div className="flex items-center gap-2" data-id="element-3022">
                      <p className="text-[13px] font-medium" style={{
                      color: isDark ? '#FFFFFF' : '#1A1A1A'
                    }} data-id="element-3023">
                        {payFromAccount.holder}
                      </p>
                      <span className="text-[13px] text-[#818285]" data-id="element-3024">-</span>
                      <p className="text-[12px]" style={{
                      color: isDark ? '#B8A89E' : '#1A1A1A'
                    }} data-id="element-3025">
                        {payFromAccount.number.replace(/\D/g, '')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="h-px w-full ml-8 mb-4" style={{
                background: isDark ? 'rgba(243,112,36,0.12)' : '#F0E8E3',
                width: 'calc(100% - 32px)'
              }} data-id="element-3026" />
                <div className="flex gap-4" data-id="element-3027">
                  <div className="shrink-0 pt-1" data-id="element-3028">
                    <div className="w-4 h-4 rounded-full bg-white border border-[#F37024]" data-id="element-3029" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3030">
                    <p className="text-[14px] font-bold mb-1" style={{
                    color: '#F37024'
                  }} data-id="element-3031">
                      Pay To
                    </p>
                    <div className="flex items-center gap-2" data-id="element-3032">
                      <p className="text-[13px] font-medium" style={{
                      color: isDark ? '#FFFFFF' : '#1A1A1A'
                    }} data-id="element-3033">
                        {selectedRecipient?.name}
                      </p>
                      <span className="text-[13px] text-[#818285]" data-id="element-3034">-</span>
                      <p className="text-[12px]" style={{
                      color: isDark ? '#B8A89E' : '#1A1A1A'
                    }} data-id="element-3035">
                        {selectedRecipient?.accountNumber}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              delay: 0.12
            }} className={`rounded-xl px-5 py-4 ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.18)]' : 'bg-[#F9F9F9]'}`} data-id="element-3036">
                <div className="flex items-center justify-between" data-id="element-3037">
                  <div className="flex-1 min-w-0" data-id="element-3038">
                    <p className={`text-[14px] font-bold ${isDark ? 'text-white' : 'text-[#5A5B5E]'}`} data-id="element-3039">
                      Save as Payee
                    </p>
                    <p className={`text-[12px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3040">
                      For quick transfer in future
                    </p>
                  </div>
                  <button onClick={() => setSavePayee(!savePayee)} className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${savePayee ? 'bg-[#F37024]' : isDark ? 'bg-[#3D2518]' : 'bg-[#D4C4BB]'}`} data-id="element-3041">
                    <motion.div className="absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm" animate={{
                    left: savePayee ? 24 : 4
                  }} transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30
                  }} data-id="element-3042" />
                  </button>
                </div>
                <AnimatePresence data-id="element-3043">
                  {savePayee && <motion.div initial={{
                  opacity: 0,
                  height: 0,
                  marginTop: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto',
                  marginTop: 14
                }} exit={{
                  opacity: 0,
                  height: 0,
                  marginTop: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-3044">
                      <label className={`text-[12px] font-bold block mb-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-3045">
                        Nickname <span className="text-red-500" data-id="element-3046">*</span>
                      </label>
                      <input type="text" value={newPayeeNickname} onChange={e => {
                    const val = e.target.value.replace(/[^a-zA-Z ]/g, '').slice(0, 40);
                    setNewPayeeNickname(val);
                    setNicknameError('');
                  }} placeholder="e.g. Asad Family" className={`w-full rounded-xl py-3 px-4 text-[14px] outline-none border ${isDark ? 'bg-[#3D2518] text-white placeholder:text-[#B8A89E]/70 border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#303030] placeholder:text-[#818285] border-[#F0E8E3]'} ${nicknameError ? 'border-red-400' : ''}`} data-id="element-3047" />
                      {nicknameError && <p className="text-red-500 text-[11px] mt-1.5 ml-1" data-id="element-3048">
                          {nicknameError}
                        </p>}
                    </motion.div>}
                </AnimatePresence>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              delay: 0.15
            }} className="rounded-xl px-5 py-4" style={{
              background: isDark ? '#2D1810' : '#FFFFFF',
              border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #F0E8E3',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 10px rgba(0,0,0,0.02)'
            }} data-id="element-3049">
                <div className="flex items-center justify-between mb-3 relative" data-id="element-3050">
                  <span className="text-[14px] font-medium" style={{
                  color: isDark ? '#FFFFFF' : '#1A1A1A'
                }} data-id="element-3051">
                    Purpose:
                  </span>
                  <button onClick={() => {
                  if (!isOwnAccountTransfer) setShowPurposeDropdown(!showPurposeDropdown);
                }} className="flex items-center gap-2 text-[13px] font-[600]" style={{
                  color: isDark ? '#FFFFFF' : '#5A5B5E'
                }} data-id="element-3052">
                    {isOwnAccountTransfer ? 'Transfer to own account' : purpose || 'Other'}
                    {!isOwnAccountTransfer && <ChevronDownIcon className="w-4 h-4" data-id="element-3053" />}
                  </button>
                  <AnimatePresence data-id="element-3054">
                    {showPurposeDropdown && <motion.div initial={{
                    opacity: 0,
                    y: -8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -8
                  }} className={`absolute top-full right-0 mt-2 w-48 rounded-xl shadow-lg z-30 max-h-56 overflow-y-auto ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.2)]' : 'bg-white border border-[#F0E8E3]'}`} data-id="element-3055">
                        {PURPOSES.map(p => <button key={p} onClick={() => {
                      setPurpose(p);
                      setShowPurposeDropdown(false);
                    }} className={`w-full text-left px-4 py-3 text-[14px] ${isDark ? 'text-white hover:bg-[#3D2518]' : 'text-[#1A1A1A] hover:bg-[#FFF7F3]'} ${p === purpose ? 'font-bold text-[#F37024]' : ''}`} data-id="element-3056">
                            {p}
                          </button>)}
                      </motion.div>}
                  </AnimatePresence>
                </div>
                <div className="h-px w-full mb-3" style={{
                background: isDark ? 'rgba(243,112,36,0.12)' : '#F0E8E3'
              }} data-id="element-3057" />
                <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Add your Comments" className="w-full text-[13px] outline-none bg-transparent" style={{
                color: isDark ? '#FFFFFF' : '#1A1A1A'
              }} data-id="element-3058" />
              </motion.div>
              <div className="flex-1 min-h-[20px]" data-id="element-3059" />
              {/* ── Pay CTA ── */}
              <div className="flex justify-center" data-id="element-3060">
                <button onClick={() => {
                if (savePayee) {
                  if (!newPayeeNickname.trim()) {
                    setNicknameError('Nickname is required');
                    return;
                  }
                  const saved: Recipient = {
                    id: `${Date.now()}`,
                    name: newPayeeNickname.trim(),
                    bank: selectedRecipient?.bank || newPayeeBank,
                    accountNumber: selectedRecipient?.accountNumber || newPayeeAccount,
                    icon: BuildingIcon,
                    color: '#F37024'
                  };
                  setPayees(prev => [...prev, saved]);
                  setSelectedRecipient(saved);
                }
                navigateTo('mpin');
              }} style={ctaStyle} data-id="element-3061">
                  Pay{' '}
                  <ArrowRightIcon className="w-5 h-5 ml-2" strokeWidth={2.5} data-id="element-3062" />
                </button>
              </div>
            </div>
          </motion.div>;
      // ─── MPIN ────────────────────────────────────────────────────────────────
      case 'mpin':
        return <motion.div key="mpin" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3063">
            <div className={`px-6 pt-6 pb-4 flex items-center justify-between sticky top-0 z-10 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3064">
              <button onClick={() => navigateTo('review', -1)} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3065">
                <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-3066" />
              </button>
              <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto" data-id="element-3067" />
              <div className="w-10" data-id="element-3068" />
            </div>
            <div className="px-6 mt-4 flex-1 flex flex-col pb-8" data-id="element-3069">
              <div className="text-center mb-10" data-id="element-3070">
                <h1 className={`text-[28px] font-bold tracking-tight mb-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3071">
                  Verify Payment
                </h1>
                <p className={`text-[15px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3072">
                  Enter your 6-digit MPIN to confirm
                </p>
              </div>
              <div className={`rounded-3xl p-6 mb-10 shadow-sm ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white'}`} data-id="element-3073">
                <div className="flex justify-between items-center mb-4" data-id="element-3074">
                  <span className={`text-[13px] font-medium ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3075">
                    Amount to send
                  </span>
                  <span className={`text-[20px] font-bold ${isDark ? 'text-white' : 'text-[#661806]'}`} data-id="element-3076">
                    Rs {parseFloat(amount || '0').toLocaleString()}
                  </span>
                </div>
                <div className={`h-px w-full mb-4 ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-3077" />
                <div className="flex items-center gap-3" data-id="element-3078">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FFF7F3]'}`} data-id="element-3079">
                    {selectedRecipient?.icon && <selectedRecipient.icon className="w-5 h-5 text-[#F37024]" strokeWidth={1.5} data-id="element-3080" />}
                  </div>
                  <div data-id="element-3081">
                    <p className={`text-[14px] font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3082">
                      {selectedRecipient?.name}
                    </p>
                    <p className={`text-[12px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3083">
                      {selectedRecipient?.bank}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-center gap-3 mb-12" data-id="element-3084">
                {[...Array(6)].map((_, i) => <div key={i} className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-bold transition-all ${mpin[i] ? isDark ? 'bg-[#3D2518] text-white border-2 border-[#F37024]' : 'bg-white text-[#303030] border-2 border-[#F37024] shadow-sm' : isDark ? 'bg-[#2D1810] border-2 border-[#3D2518]' : 'bg-[#F5EBE6] border-2 border-transparent'}`} data-id="element-3085">
                    {mpin[i] ? '•' : ''}
                  </div>)}
              </div>
              <div className="mt-auto grid grid-cols-3 gap-y-6 gap-x-4 max-w-[300px] mx-auto w-full" data-id="element-3086">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => <button key={num} onClick={() => {
                if (mpin.length < 6) setMpin(prev => prev + num);
              }} className={`h-14 rounded-2xl text-[24px] font-medium flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-white hover:bg-[#2D1810]' : 'text-[#303030]'}`} data-id="element-3087">
                    {num}
                  </button>)}
                <button onClick={() => setBiometricPhase('scanning')} className={`h-14 rounded-2xl flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-[#F37024] hover:bg-[#2D1810]' : 'text-[#F37024]'}`} data-id="element-3088">
                  <FingerprintIcon className="w-8 h-8" strokeWidth={1.5} data-id="element-3089" />
                </button>
                <button onClick={() => {
                if (mpin.length < 6) setMpin(prev => prev + '0');
              }} className={`h-14 rounded-2xl text-[24px] font-medium flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-white hover:bg-[#2D1810]' : 'text-[#303030]'}`} data-id="element-3090">
                  0
                </button>
                <button onClick={() => setMpin(prev => prev.slice(0, -1))} className={`h-14 rounded-2xl flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-[#B8A89E] hover:bg-[#2D1810]' : 'text-[#818285]'}`} data-id="element-3091">
                  <ArrowLeftIcon className="w-7 h-7" strokeWidth={2} data-id="element-3092" />
                </button>
              </div>
              <AnimatePresence data-id="element-3093">
                {biometricPhase !== 'entering_mpin' && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="fixed inset-0 z-50 flex flex-col items-center justify-center" style={{
                background: isDark ? 'linear-gradient(180deg, rgba(26,14,8,0.95) 0%, rgba(45,24,16,0.95) 100%)' : 'linear-gradient(180deg, rgba(255,247,243,0.95) 0%, rgba(253,238,229,0.95) 100%)',
                backdropFilter: 'blur(10px)'
              }} data-id="element-3094">
                    <AnimatePresence mode="wait" data-id="element-3095">
                      {biometricPhase === 'scanning' && <motion.div initial={{
                    opacity: 0,
                    scale: 0.9
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.9
                  }} className="flex flex-col items-center gap-6" data-id="element-3096">
                          <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} className="w-24 h-24 rounded-full flex items-center justify-center" style={{
                      background: 'rgba(243, 112, 36, 0.12)',
                      border: '2px solid rgba(243, 112, 36, 0.3)'
                    }} data-id="element-3097">
                            {scanType === 'thumb' ? <FingerprintIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-3098" /> : <ScanFaceIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-3099" />}
                          </motion.div>
                          <div className="text-center" data-id="element-3100">
                            <p className={`text-[18px] font-bold mb-1 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3101">
                              {scanType === 'thumb' ? 'Scanning Fingerprint' : 'Scanning Face'}
                            </p>
                            <p className={`text-[13px] ${isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]'}`} data-id="element-3102">
                              {scanType === 'thumb' ? 'Place your finger on the sensor' : 'Look at the camera'}
                            </p>
                          </div>
                          <button onClick={() => setBiometricPhase('entering_mpin')} className={`mt-4 text-[14px] font-bold underline ${isDark ? 'text-[#F37024]' : 'text-[#661806]'}`} data-id="element-3103">
                            Use MPIN Instead
                          </button>
                        </motion.div>}
                      {biometricPhase === 'verified' && <motion.div initial={{
                    opacity: 0,
                    scale: 0.9
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.9
                  }} className="flex flex-col items-center gap-6" data-id="element-3104">
                          <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center" style={{
                      border: '2px solid rgba(34, 197, 94, 0.3)'
                    }} data-id="element-3105">
                            <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-3106" />
                          </motion.div>
                          <div className="text-center" data-id="element-3107">
                            <p className={`text-[18px] font-bold mb-1 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3108">
                              Verified Successfully
                            </p>
                            <p className={`text-[13px] ${isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]'}`} data-id="element-3109">
                              Processing payment...
                            </p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>;
      // ─── PROCESSING ──────────────────────────────────────────────────────────
      case 'processing':
        return <motion.div key="processing" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className={`w-full min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3110">
            <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'linear'
          }} className="w-20 h-20 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024] mb-6" data-id="element-3111" />
            <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3112">
              Processing Payment
            </h2>
            <p className={`text-[15px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3113">
              Please wait while we process your transaction
            </p>
          </motion.div>;
      // ─── SUCCESS ─────────────────────────────────────────────────────────────
      case 'success':
        const now = new Date();
        const formattedDate = now.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }).replace(/ /g, '-');
        const formattedTime = now.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
        const txnId = '78343846823';
        const amountNum = Number(amount.replace(/,/g, '')) || 0;
        const payFromDigits = payFromAccount.number.replace(/[^\d]/g, '');
        return <motion.div key="success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full min-h-screen flex flex-col relative overflow-hidden" style={{
          background: '#F2F2F2'
        }} data-id="element-3114">
            {!isDark && <div aria-hidden className="absolute pointer-events-none" style={{
            top: '-5%',
            left: '-15%',
            width: '130%',
            height: '52%',
            background: 'radial-gradient(ellipse 60% 55% at 52% 42%, rgba(243,130,50,0.38) 0%, rgba(243,150,80,0.20) 38%, rgba(243,112,36,0.06) 65%, transparent 80%)',
            filter: 'blur(22px)',
            zIndex: 0
          }} data-id="element-3115" />}
            {isDark && <div aria-hidden className="absolute pointer-events-none" style={{
            top: '-5%',
            left: '-15%',
            width: '130%',
            height: '52%',
            background: 'radial-gradient(ellipse 60% 55% at 52% 42%, rgba(243,112,36,0.18) 0%, transparent 70%)',
            filter: 'blur(22px)',
            zIndex: 0
          }} data-id="element-3116" />}
            <div className="px-5 pt-6 pb-4 flex items-center justify-between relative z-10" data-id="element-3117">
              <h1 className={`text-[18px] font-regular ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-3118">
                Receipt
              </h1>
              <button onClick={onNavigateHome} className={`p-2 -mr-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-3119">
                <HomeIcon className="w-6 h-6" strokeWidth={1.8} data-id="element-3120" />
              </button>
            </div>
            <div className="px-5 flex-1 flex flex-col pb-6 space-y-4 relative z-10" data-id="element-3121">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.4
            }} className="flex flex-col items-center relative overflow-hidden mx-auto" style={{
              width: '357px',
              height: '257px',
              borderRadius: '15px',
              background: isDark ? 'rgba(45,24,16,0.82)' : 'rgba(255,255,255,0.72)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1.5px solid #F37024',
              boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)' : '0 8px 32px rgba(243,112,36,0.10), inset 0 1px 0 rgba(255,255,255,0.9)',
              padding: '28px 24px',
              justifyContent: 'center'
            }} data-id="element-3122">
                <div className="w-14 h-14 rounded-full bg-[#22C55E] flex items-center justify-center mb-3 z-10" style={{
                boxShadow: '0 4px 12px rgba(34,197,94,0.35)'
              }} data-id="element-3123">
                  <CheckIcon className="w-7 h-7 text-white" strokeWidth={3} data-id="element-3124" />
                </div>
                <p className="text-[15px] font-medium mb-5 z-10" style={{
                color: isDark ? '#C4B5AA' : '#5A5B5E'
              }} data-id="element-3125">
                  Success
                </p>
                <div className="flex items-baseline justify-center gap-2 mb-5 z-10" data-id="element-3126">
                  <span className="text-[22px] font-medium" style={{
                  color: isDark ? '#B8A89E' : '#9E9E9E'
                }} data-id="element-3127">
                    PKR
                  </span>
                  <span className="font-bold leading-none" style={{
                  fontSize: '52px',
                  color: isDark ? '#FFFFFF' : '#5A1408',
                  letterSpacing: '-1.5px'
                }} data-id="element-3128">
                    {amountNum.toLocaleString('en-US')}
                  </span>
                </div>
                <p className="text-[13px] font-medium z-10 mb-1" style={{
                color: isDark ? '#C4B5AA' : '#303030'
              }} data-id="element-3129">
                  {formattedDate}, {formattedTime}
                </p>
                <p className="text-[13px] font-medium z-10" style={{
                color: isDark ? '#C4B5AA' : '#303030'
              }} data-id="element-3130">
                  Ref# {txnId}
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.1
            }} className="rounded-2xl px-5 py-5" style={{
              background: isDark ? '#2D1810' : '#FFFFFF',
              border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #E8E8E8',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.04)'
            }} data-id="element-3131">
                <div className="flex items-start justify-between mb-5" data-id="element-3132">
                  <span className="text-[14px] font-bold mt-0.5" style={{
                  color: '#5A1408'
                }} data-id="element-3133">
                    To:
                  </span>
                  <div className="text-right" data-id="element-3134">
                    <p className="text-[14px] font-medium" style={{
                    color: isDark ? '#FFFFFF' : '#1A1A1A'
                  }} data-id="element-3135">
                      {selectedRecipient?.name || 'Recipient'}
                    </p>
                    <p className="text-[13px] mt-0.5" style={{
                    color: isDark ? '#B8A89E' : '#5A5B5E'
                  }} data-id="element-3136">
                      *********
                      {selectedRecipient?.accountNumber?.slice(-4) || '1234'}
                    </p>
                  </div>
                </div>
                <div className="h-px w-full mb-5" style={{
                background: isDark ? 'rgba(243,112,36,0.1)' : '#F0F0F0'
              }} data-id="element-3137" />
                <div className="flex items-start justify-between" data-id="element-3138">
                  <span className="text-[14px] font-bold mt-0.5" style={{
                  color: '#5A1408'
                }} data-id="element-3139">
                    From:
                  </span>
                  <div className="text-right" data-id="element-3140">
                    <p className="text-[14px] font-medium" style={{
                    color: isDark ? '#FFFFFF' : '#1A1A1A'
                  }} data-id="element-3141">
                      {payFromAccount.holder}
                    </p>
                    <p className="text-[13px] mt-0.5" style={{
                    color: isDark ? '#B8A89E' : '#5A5B5E'
                  }} data-id="element-3142">
                      *********{payFromDigits.slice(-4)}
                    </p>
                  </div>
                </div>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.15
            }} className="rounded-2xl px-5 py-4" style={{
              background: isDark ? '#2D1810' : '#FFFFFF',
              border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #E8E8E8',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.04)'
            }} data-id="element-3143">
                <button onClick={() => setShowAdditionalInfo(!showAdditionalInfo)} className="w-full flex items-center justify-between mb-1" data-id="element-3144">
                  <span className="text-[14px] font-medium" style={{
                  color: isDark ? '#FFFFFF' : '#1A1A1A'
                }} data-id="element-3145">
                    Additional Information
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 transition-transform ${showAdditionalInfo ? 'rotate-180' : ''}`} style={{
                  color: isDark ? '#B8A89E' : '#1A1A1A'
                }} data-id="element-3146" />
                </button>
                <AnimatePresence initial={false} data-id="element-3147">
                  {showAdditionalInfo && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-3148">
                      <div className="pt-4 space-y-4" data-id="element-3149">
                        {[{
                      label: 'Cannel',
                      value: transferMethod === 'raast' ? 'RAAST' : '1LINK'
                    }, {
                      label: 'Transaction Fee',
                      value: 'PKR 0'
                    }, {
                      label: 'Destination Bank',
                      value: selectedRecipient?.bank || newPayeeBank || 'Meezan Bank'
                    }, {
                      label: 'Purpose of Transfer',
                      value: purpose || 'Other'
                    }, {
                      label: 'Comments',
                      value: note || 'Advance Payment ...'
                    }].map(item => <div key={item.label} className="flex justify-between items-center" data-id="element-3150">
                            <span className="text-[13px] font-bold" style={{
                        color: '#F37024'
                      }} data-id="element-3151">
                              {item.label}
                            </span>
                            <span className="text-[13px] font-medium" style={{
                        color: isDark ? '#FFFFFF' : '#1A1A1A'
                      }} data-id="element-3152">
                              {item.value}
                            </span>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>
              <div className="flex-1 min-h-[12px]" data-id="element-3153" />
              <div className="flex justify-center mb-2" data-id="element-3154">
                <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP" className="h-10 object-contain" style={{
                filter: isDark ? 'brightness(0) invert(1)' : 'none'
              }} data-id="element-3155" />
              </div>
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.2
            }} className="flex items-start justify-around pt-1 pb-2" data-id="element-3156">
                <button className="flex flex-col items-center gap-1.5 px-4 active:scale-95 transition-transform" data-id="element-3157">
                  <ShareIcon style={{
                  width: 26,
                  height: 26,
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} strokeWidth={1.8} data-id="element-3158" />
                  <span className="text-[12px] font-medium" style={{
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} data-id="element-3159">
                    Share
                  </span>
                </button>
                <button className="flex flex-col items-center gap-1.5 px-4 active:scale-95 transition-transform" data-id="element-3160">
                  <ImageIcon style={{
                  width: 26,
                  height: 26,
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} strokeWidth={1.8} data-id="element-3161" />
                  <span className="text-[12px] font-medium" style={{
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} data-id="element-3162">
                    Save Image
                  </span>
                </button>
                <button onClick={onNavigateHome} className="flex flex-col items-center gap-1.5 px-4 active:scale-95 transition-transform" data-id="element-3163">
                  <ArrowRightIcon style={{
                  width: 26,
                  height: 26,
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} strokeWidth={1.8} data-id="element-3164" />
                  <span className="text-[12px] font-medium" style={{
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} data-id="element-3165">
                    Logout
                  </span>
                </button>
              </motion.div>
            </div>
          </motion.div>;
    }
  };
  return <div className={`w-full min-h-screen relative overflow-hidden ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3166">
      <AnimatePresence mode="wait" custom={direction} data-id="element-3167">
        {renderStep()}
      </AnimatePresence>
      <NotificationPanel isOpen={showNotifications} onClose={() => setShowNotifications(false)} data-id="element-3168" />
    </div>;
}